import React, { useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../../sectepe-ui/index';
import leadSyncService, { Lead, LemlistCampaign, LemlistSyncPreview, SyncToLemlistResult } from '../../services/leadSyncService';
import { EmptyState, StatusBadge } from '../../components/ui';
import { Icon } from '../../components/icons';
import { datum, fehlerText } from '../format';
import { ErgebnisTabelle } from './ErgebnisTabelle';
import { leadName, lemlistStatus } from './leadStatus';

interface Props {
  leads: Lead[];
  ausgewaehlt: string[];
  kampagnen: LemlistCampaign[];
  laedtKampagnen: boolean;
  onKampagnenLaden: () => void;
  onGeladen: () => Promise<void> | void;
  onFehler: (text: string | null) => void;
  /**
   * versand.ausfuehren: Leads in eine Kampagne aufnehmen. Ohne das Recht
   * bleiben Vorschau und Kampagnenstand (versand.lesen), die Sende-Knoepfe
   * fehlen -- wie im Backend (POST /lemlist/sync braucht das Recht,
   * /lemlist/preview nicht).
   */
  darfSenden: boolean;
  /** Fehlt der Handler (kein Recht versand.ausfuehren), gibt es den Knopf nicht. */
  onEntfernen?: (lead: Lead) => void;
}

export const KampagnenAuswahl: React.FC<{
  kampagnen: LemlistCampaign[]; wert: string; onChange: (v: string) => void; laedt: boolean; onLaden: () => void; id: string;
}> = ({ kampagnen, wert, onChange, laedt, onLaden, id }) => (
  <label className="lead-panel__feld" htmlFor={id}>
    <span className="lead-panel__feldkopf">
      Lemlist-Kampagne
      <button type="button" className="btn btn-sm btn-ghost" onClick={onLaden} disabled={laedt} aria-label="Kampagnen neu laden">
        <Icon name="refresh" size={12} /> {laedt ? 'lädt…' : 'neu laden'}
      </button>
    </span>
    <select id={id} value={wert} onChange={(e) => onChange(e.target.value)} disabled={laedt}>
      <option value="">Kampagne wählen…</option>
      {kampagnen.map((k) => <option key={k._id} value={k._id}>{k.name}{k.status ? ` (${k.status})` : ''}</option>)}
    </select>
  </label>
);

/** Lemlist: ausgewaehlte Leads in eine Kampagne schicken, laufende Zuordnungen sehen. */
export const KampagnenPanel: React.FC<Props> = ({
  leads, ausgewaehlt, kampagnen, laedtKampagnen, onKampagnenLaden, onGeladen, onFehler, darfSenden, onEntfernen,
}) => {
  const { push } = useToast();
  const [kampagne, setKampagne] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState<SyncToLemlistResult | null>(null);
  // Nachrichten-Vorlage fuer die gerenderte Vorschau vor dem Abgleich.
  const [vorlage, setVorlage] = useState({
    subject: '{{icebreakerSubject}}',
    body: 'Hi {{firstName}},\n\n{{icebreaker}}\n\nHätten Sie nächste Woche 15 Minuten für einen kurzen Austausch?\n\nBeste Grüße',
  });
  const [vorschau, setVorschau] = useState<LemlistSyncPreview | null>(null);
  const [laedtVorschau, setLaedtVorschau] = useState(false);
  const inKampagne = leads.filter((l) => l.lemlistCampaignId);
  const kampagnenName = (id: string) => kampagnen.find((k) => k._id === id)?.name ?? id;

  const senden = async () => {
    setLaeuft(true);
    onFehler(null);
    try {
      const r = await leadSyncService.syncToLemlist(ausgewaehlt, kampagne);
      setErgebnis(r);
      setVorschau(null);
      push({ message: `${r.synced} Leads in „${kampagnenName(kampagne)}“ aufgenommen.`, severity: r.failed ? 'warning' : 'success' });
      await onGeladen();
    } catch (e) {
      onFehler(fehlerText(e, 'Lemlist-Abgleich fehlgeschlagen'));
    } finally {
      setLaeuft(false);
    }
  };

  const vorschauLaden = async () => {
    setLaedtVorschau(true);
    onFehler(null);
    try {
      setVorschau(await leadSyncService.previewLemlistSync(ausgewaehlt, undefined, vorlage));
    } catch (e) {
      onFehler(fehlerText(e, 'Fehler bei der Sync-Vorschau'));
    } finally {
      setLaedtVorschau(false);
    }
  };

  return (
    <div className="lead-panel">
      <section className="card" aria-label="In Kampagne aufnehmen">
        <h3 className="card-title">In eine Kampagne aufnehmen</h3>
        <p className="card-description">Die ausgewählten Leads werden in Lemlist angelegt und bekommen die Sequenz der Kampagne. Was zurückkommt, landet unter Antworten.</p>
        <KampagnenAuswahl id="kampagne-sync" kampagnen={kampagnen} wert={kampagne} onChange={setKampagne} laedt={laedtKampagnen} onLaden={onKampagnenLaden} />
        <details className="lead-panel__feld">
          <summary>Nachrichten-Vorlage (für die gerenderte Vorschau)</summary>
          <p className="text-xs text-secondary">
            Variablen: <code>{'{{firstName}}'}</code> <code>{'{{lastName}}'}</code> <code>{'{{companyName}}'}</code>{' '}
            <code>{'{{jobTitle}}'}</code> <code>{'{{icebreaker}}'}</code> <code>{'{{icebreakerSubject}}'}</code>{' '}
            <code>{'{{icebreakerLinkedin}}'}</code> <code>{'{{website}}'}</code> <code>{'{{location}}'}</code>
          </p>
          <label className="lead-panel__feld">
            Betreff-Vorlage
            <input type="text" value={vorlage.subject} onChange={(e) => setVorlage({ ...vorlage, subject: e.target.value })} />
          </label>
          <label className="lead-panel__feld">
            Text-Vorlage
            <textarea className="form-textarea" rows={6} value={vorlage.body} onChange={(e) => setVorlage({ ...vorlage, body: e.target.value })} />
          </label>
        </details>
        <div className="btn-group">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={vorschauLaden}
            disabled={laedtVorschau || ausgewaehlt.length === 0}
            title="Zeigt vor dem Abgleich die gerenderte Nachricht und die Variablen je Lead"
          >
            <Icon name="eye" size={14} /> {laedtVorschau ? 'Lade Vorschau…' : 'Nachrichten-Vorschau'}
          </button>
          {darfSenden && (
            <button type="button" className="btn btn-primary" onClick={senden} disabled={laeuft || ausgewaehlt.length === 0 || !kampagne}>
              <Icon name="send" size={14} /> {laeuft ? 'Sendet…' : `${ausgewaehlt.length} Leads aufnehmen`}
            </button>
          )}
        </div>
        {ausgewaehlt.length === 0 && <p className="text-xs text-secondary">Oben in der Liste Leads auswählen.</p>}
        {!darfSenden && <p className="text-xs text-muted">In Kampagnen aufnehmen darf, wer versenden darf (Vertrieb, Inhaber:in).</p>}
      </section>

      <Modal open={Boolean(vorschau)} onOpenChange={(o) => { if (!o) setVorschau(null); }}>
        {vorschau && (
          <ModalContent className="detail-modal">
            <ModalHeader>
              <ModalTitle>Lemlist-Vorschau: Nachricht und Variablen</ModalTitle>
              <ModalDescription>So kommen die ausgewählten Leads in Lemlist an.</ModalDescription>
            </ModalHeader>
            <ModalBody>
              <div className="lead-ergebnis__summe">
                <StatusBadge tone="info">Gesamt: {vorschau.summary.total}</StatusBadge>
                <StatusBadge tone="success">Synchronisierbar: {vorschau.summary.syncable}</StatusBadge>
                {vorschau.summary.blocked > 0 && <StatusBadge tone="danger">Blockiert: {vorschau.summary.blocked}</StatusBadge>}
                {vorschau.summary.missingEmailIcebreaker > 0 && (
                  <StatusBadge tone="warning">Ohne {'{{icebreaker}}'}: {vorschau.summary.missingEmailIcebreaker}</StatusBadge>
                )}
                {vorschau.summary.missingLinkedinIcebreaker > 0 && (
                  <StatusBadge tone="warning">Ohne {'{{icebreakerLinkedin}}'}: {vorschau.summary.missingLinkedinIcebreaker}</StatusBadge>
                )}
              </div>
              <div className="table-container" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <table className="table">
                  <thead><tr><th>Lead</th><th>Gerenderte Nachricht</th><th>Variablen (an Lemlist)</th><th>Hinweise</th></tr></thead>
                  <tbody>
                    {vorschau.leads.map((l) => {
                      const felder = Object.entries(l.customFields).filter(([, v]) => v);
                      return (
                        <tr key={l.id} className={l.blocking ? 'zeile-blockiert' : undefined}>
                          <td>
                            <strong>{l.email}</strong>
                            <div className="text-xs text-secondary">{[l.name, l.company].filter(Boolean).join(' · ') || '—'}</div>
                          </td>
                          <td style={{ minWidth: 240 }}>
                            {l.renderedSubject === undefined && l.renderedBody === undefined ? (
                              <span className="text-xs text-secondary">keine Vorlage</span>
                            ) : (
                              <div className="text-xs">
                                <strong>Betreff: {l.renderedSubject || '(leer)'}</strong>
                                <div style={{ whiteSpace: 'pre-wrap' }} className="text-secondary">{l.renderedBody || '(leer)'}</div>
                              </div>
                            )}
                          </td>
                          <td>
                            {felder.length === 0 ? (
                              <span className="text-xs text-secondary">keine</span>
                            ) : felder.map(([k, v]) => (
                              <div key={k} className="text-xs">
                                <code>{`{{${k}}}`}</code>: <span className="text-secondary">{v.length > 80 ? `${v.slice(0, 80)}…` : v}</span>
                              </div>
                            ))}
                          </td>
                          <td>
                            {l.warnings.length === 0 ? (
                              <StatusBadge tone="success">OK</StatusBadge>
                            ) : (
                              <ul className="text-xs" style={{ margin: 0, paddingLeft: '1rem' }}>
                                {l.warnings.map((w, i) => <li key={i} className={l.blocking ? 'text-error' : 'text-secondary'}>{w}</li>)}
                              </ul>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </ModalBody>
            <ModalFooter>
              {darfSenden && !kampagne && <span className="text-xs text-secondary">Kampagne oben wählen, um aufzunehmen.</span>}
              <button type="button" className="btn btn-ghost" onClick={() => setVorschau(null)}>Schließen</button>
              {darfSenden && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={senden}
                  disabled={laeuft || !kampagne || vorschau.summary.syncable === 0}
                >
                  <Icon name="send" size={14} /> {laeuft ? 'Sendet…' : 'Jetzt aufnehmen'}
                </button>
              )}
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>

      {ergebnis && <ErgebnisTabelle titel="Ergebnis des Abgleichs" gut={ergebnis.synced} gutLabel="aufgenommen" schlecht={ergebnis.failed} details={ergebnis.details} />}

      {inKampagne.length === 0 ? (
        <EmptyState compact icon="megaphone" title="Noch kein Lead in einer Kampagne" description="Sobald Leads aufgenommen sind, stehen sie hier mit Kampagne und Lemlist-Status." />
      ) : (
        <section className="card" aria-label="In Kampagnen">
          <h3 className="card-title">In Kampagnen ({inKampagne.length})</h3>
          <div className="table-container">
            <table className="table">
              <thead><tr><th>Lead</th><th>Kampagne</th><th>Lemlist-Status</th><th>seit</th><th /></tr></thead>
              <tbody>
                {inKampagne.map((l) => {
                  const st = lemlistStatus(l.lemlistStatus);
                  return (
                    <tr key={l.id}>
                      <td><strong>{leadName(l)}</strong><div className="text-xs text-secondary">{l.email}</div></td>
                      <td>{kampagnenName(l.lemlistCampaignId!)}</td>
                      <td>{st ? <StatusBadge tone={st.tone}>{st.label}</StatusBadge> : '—'}</td>
                      <td className="text-sm">{datum(l.lemlistSyncedAt)}</td>
                      <td className="r">{onEntfernen && <button type="button" className="btn btn-sm btn-ghost" onClick={() => onEntfernen(l)}>aus Kampagne</button>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
};
